/**
 * CloseAdvanceSlideOver — settle a staff advance and flip its
 * status to `'settled'`.
 *
 * The flow it presents depends on the live balance:
 *
 *   balance > 0  — engineer is holding leftover cash. User
 *                  picks between "Record cash refund" (the
 *                  default; writes a payment_in child) or
 *                  "Write off (no refund)" which forces the
 *                  close without a refund txn.
 *   balance < 0  — engineer overspent. The studio owes them.
 *                  Single CTA: "Reimburse ₹|balance|" which
 *                  writes a payment_out child to the staff
 *                  member and immediately closes.
 *   balance == 0 — exact reconciliation already happened (or
 *                  there are no bills). Single CTA:
 *                  "Mark settled" which just flips the parent.
 *
 * Every action wraps in runTransaction so concurrent writes
 * can't leave the parent in a half-settled state.
 */
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import {
  closeAdvance,
  computeAdvanceBalance,
  recordAdvanceRefund,
  recordAdvanceReimbursement,
} from '@/lib/transactions';
import { useAdvanceChildren } from '@/lib/useAdvances';
import type { TransactionItem } from '@/lib/useProjectTransactions';

function fmtInr(n: number | undefined | null): string {
  if (!n) return '₹0';
  return `₹${Math.round(Math.abs(n)).toLocaleString('en-IN')}`;
}

export type CloseAdvanceSlideOverProps = {
  open: boolean;
  onClose: () => void;
  /** The parent staff_advance transaction being closed.
   *  `null` keeps the slide-over closed regardless of `open`. */
  advance: TransactionItem | null;
  onClosed?: () => void;
};

type Choice = 'refund' | 'writeoff';

export function CloseAdvanceSlideOver({
  open,
  onClose,
  advance,
  onClosed,
}: CloseAdvanceSlideOverProps) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only meaningful in the over-balance (refund) case — radio
  // pick between recording the refund vs writing off.
  const [choice, setChoice] = useState<Choice>('refund');

  const { children, loading } = useAdvanceChildren(
    advance?.orgId ?? null,
    advance?.id ?? null,
  );

  const live = useMemo(() => {
    if (!advance) return null;
    return computeAdvanceBalance(
      { amount: advance.amount ?? 0, advanceStatus: advance.advanceStatus },
      children.map((c) => ({
        type: c.type,
        amount: c.amount ?? 0,
        advanceRole: c.advanceRole,
        workflowStatus: c.workflowStatus,
      })),
    );
  }, [advance, children]);

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    setError(null);
    setChoice('refund');
  }, [open, advance?.id]);

  // Body scroll lock + Esc.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    if (!advance) return setError('No advance selected.');
    if (!user) return setError('You must be signed in.');
    if (!live) return setError('Advance balance is still loading.');

    setBusy(true);
    try {
      if (live.balance > 0) {
        // Engineer holding leftover cash.
        if (choice === 'refund') {
          await recordAdvanceRefund({
            parentTransactionId: advance.id,
            orgId: advance.orgId,
            projectId: advance.projectId,
            staffPartyId: advance.partyId ?? '',
            staffPartyName: advance.partyName ?? 'Staff member',
            amount: live.balance,
            createdBy: user.uid,
          });
          await closeAdvance({ parentTransactionId: advance.id });
        } else {
          // Write off without recording a refund.
          await closeAdvance({
            parentTransactionId: advance.id,
            force: true,
          });
        }
      } else if (live.balance < 0) {
        // Engineer overspent — studio owes them.
        await recordAdvanceReimbursement({
          parentTransactionId: advance.id,
          orgId: advance.orgId,
          projectId: advance.projectId,
          staffPartyId: advance.partyId ?? '',
          staffPartyName: advance.partyName ?? 'Staff member',
          amount: Math.abs(live.balance),
          createdBy: user.uid,
        });
        await closeAdvance({ parentTransactionId: advance.id });
      } else {
        // Exact match — just mark settled.
        await closeAdvance({ parentTransactionId: advance.id });
      }
      onClosed?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to close advance.');
      setBusy(false);
    }
  };

  // Derive UI copy + primary CTA from the live balance.
  const ctaText = !live
    ? 'Loading…'
    : live.balance > 0
      ? choice === 'refund'
        ? `Refund ${fmtInr(live.balance)} & close`
        : `Write off ${fmtInr(live.balance)} & close`
      : live.balance < 0
        ? `Reimburse ${fmtInr(live.balance)} & close`
        : 'Mark settled';

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={handleClose}
        aria-hidden
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        role="dialog"
        aria-label="Close staff advance"
        aria-hidden={!open}
        style={{ maxWidth: 460 }}
      >
        <div className="slide-header">
          <h2 className="slide-title">Close advance</h2>
          <button
            type="button"
            className="slide-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {/* Balance snapshot */}
          {advance ? (
            <div
              className="card"
              style={{
                padding: 16,
                marginBottom: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-tertiary)',
                  letterSpacing: 0.4,
                }}
              >
                ADVANCE BALANCE
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Given to {advance.partyName ?? '—'}
                </span>
                <strong>{fmtInr(advance.amount)}</strong>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Bills filed ({children.length})
                </span>
                <span>{live ? fmtInr(live.spent) : '—'}</span>
              </div>
              {live && live.refunded > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    Already refunded
                  </span>
                  <span>{fmtInr(live.refunded)}</span>
                </div>
              ) : null}
              {live && live.reimbursed > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    Already reimbursed
                  </span>
                  <span>{fmtInr(live.reimbursed)}</span>
                </div>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontVariantNumeric: 'tabular-nums',
                  paddingTop: 8,
                  borderTop: '1px solid var(--border)',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 700 }}>
                  {!live
                    ? 'Balance'
                    : live.balance > 0
                      ? 'Engineer is holding'
                      : live.balance < 0
                        ? 'Studio owes engineer'
                        : 'Balance'}
                </span>
                <strong
                  style={{
                    fontSize: 18,
                    color:
                      !live || live.balance === 0
                        ? 'var(--success)'
                        : live.balance < 0
                          ? 'var(--critical)'
                          : 'var(--warning)',
                  }}
                >
                  {live ? fmtInr(live.balance) : '—'}
                </strong>
              </div>
            </div>
          ) : null}

          {/* Choice radio — only when balance > 0 */}
          {live && live.balance > 0 ? (
            <div
              className="card"
              style={{
                padding: 12,
                marginBottom: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  checked={choice === 'refund'}
                  onChange={() => setChoice('refund')}
                  disabled={busy}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    Record cash refund of {fmtInr(live.balance)}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    Adds a payment-in row from the staff member — your
                    cash balance goes up.
                  </div>
                </div>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  checked={choice === 'writeoff'}
                  onChange={() => setChoice('writeoff')}
                  disabled={busy}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    Write off (no refund)
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    Closes the advance without recording cash back.
                    Use only when the leftover stays with the staff
                    or has been settled outside the app.
                  </div>
                </div>
              </label>
            </div>
          ) : null}

          {/* Reimbursement explainer */}
          {live && live.balance < 0 ? (
            <div
              className="card"
              style={{
                padding: 12,
                marginBottom: 14,
                background: 'var(--critical-bg)',
                border: '1px solid #FECACA',
                color: 'var(--critical-text)',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                Reimbursement
              </div>
              <div style={{ fontSize: 12 }}>
                The bills total {fmtInr(live.spent)} but only{' '}
                {fmtInr(advance?.amount)} was given as advance. Closing
                this advance will record a payment-out of{' '}
                <strong>{fmtInr(live.balance)}</strong> to{' '}
                {advance?.partyName ?? 'the staff member'} so the books
                match what was actually spent.
              </div>
            </div>
          ) : null}

          {error ? (
            <div
              className="card"
              style={{
                padding: 12,
                marginBottom: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                borderColor: 'var(--critical)',
              }}
            >
              <i className="ph ph-warning-circle" /> {error}
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 6,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={handleClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              style={{
                flex: 1,
                justifyContent: 'center',
                background:
                  live && live.balance < 0
                    ? 'var(--critical)'
                    : live && live.balance > 0 && choice === 'refund'
                      ? 'var(--success)'
                      : 'var(--brand)',
                borderColor:
                  live && live.balance < 0
                    ? 'var(--critical)'
                    : live && live.balance > 0 && choice === 'refund'
                      ? 'var(--success)'
                      : 'var(--brand)',
              }}
              onClick={handleSubmit}
              disabled={busy || loading || !live}
            >
              {busy ? 'Closing…' : ctaText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
